import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { FormGroup, Select, Button } from '../components/Form';
import { logError } from '../lib/logError';
import {
    EVENTOS,
    DESTINOS,
    fetchGhlPipelines,
    fetchGhlCustomFields,
    loadStageMap,
    saveStageMap,
    loadFieldMap,
    upsertFieldMapRow,
    deactivateFieldMapRow,
} from '../services/ghlIntegracao';

const cell = { padding: '0.5rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: '0.8125rem' };
const headCell = { ...cell, color: 'var(--text-secondary)', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em' };

/**
 * Configuração da integração GHL:
 * 1) Colunas-Gatilho — qual estágio do CRM dispara qual evento (agendamento, no-show, venda...)
 * 2) De-Para de Campos — qual campo personalizado do GHL alimenta qual coluna no banco
 * A captura em si roda no dashboard (polling); o desk só edita a configuração.
 */
export default function IntegracaoGHL() {
    const [pipelines, setPipelines] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [stageRows, setStageRows] = useState([]);        // {pipeline_id, stage_id, nome, pipeline_nome, evento, rodada, existente}
    const [fieldRows, setFieldRows] = useState([]);        // linhas ativas de ghl_field_map
    const [newField, setNewField] = useState({ ghl_field_id: '', evento: 'agendamento', destino: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', text: '' });

    const flash = (type, text) => {
        setStatus({ type, text });
        setTimeout(() => setStatus({ type: '', text: '' }), 4000);
    };

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const [pls, cfs, mapa, fmap] = await Promise.all([
                fetchGhlPipelines(),
                fetchGhlCustomFields(),
                loadStageMap(),
                loadFieldMap(),
            ]);
            setPipelines(pls);
            setCustomFields(cfs);
            const byStage = new Map(mapa.map((m) => [`${m.pipeline_id}:${m.stage_id}`, m]));
            const rows = [];
            for (const p of pls) {
                for (const s of p.stages ?? []) {
                    const atual = byStage.get(`${p.id}:${s.id}`);
                    rows.push({
                        pipeline_id: p.id,
                        pipeline_nome: p.name,
                        stage_id: s.id,
                        nome: s.name,
                        evento: atual?.ativo ? atual.evento : '',
                        rodada: atual?.rodada ?? null,
                        existente: !!atual,
                    });
                }
            }
            setStageRows(rows);
            setFieldRows(fmap.filter((f) => f.ativo));
        } catch (err) {
            logError(err, { fn: 'IntegracaoGHL.carregar' });
            flash('error', 'Falha ao carregar dados do GHL/Supabase. Veja o console.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const setStage = (stage_id, patch) =>
        setStageRows((rows) => rows.map((r) => (r.stage_id === stage_id ? { ...r, ...patch } : r)));

    const handleSalvarGatilhos = async () => {
        setIsSaving(true);
        try {
            await saveStageMap(stageRows);
            flash('success', '✓ Colunas-gatilho salvas. O pipeline de captura usa a nova configuração no próximo ciclo (~1 min).');
        } catch {
            flash('error', 'Erro ao salvar colunas-gatilho.');
        } finally {
            setIsSaving(false);
        }
    };

    const fieldById = useMemo(() => new Map(customFields.map((f) => [f.id, f])), [customFields]);

    const handleAddField = async () => {
        const destino = DESTINOS.find((d) => `${d.tabela}.${d.coluna}` === newField.destino);
        const cf = fieldById.get(newField.ghl_field_id);
        if (!cf || !destino) { flash('error', 'Selecione o campo do GHL e o destino.'); return; }
        setIsSaving(true);
        try {
            await upsertFieldMapRow({
                evento: newField.evento,
                ghl_field_id: cf.id,
                ghl_field_key: cf.fieldKey,
                destino_tabela: destino.tabela,
                destino_coluna: destino.coluna,
                tipo_esperado: cf.dataType,
            });
            setNewField({ ghl_field_id: '', evento: newField.evento, destino: '' });
            setFieldRows(await loadFieldMap().then((f) => f.filter((x) => x.ativo)));
            flash('success', '✓ De-para adicionado.');
        } catch {
            flash('error', 'Erro ao salvar o de-para.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveField = async (id) => {
        setIsSaving(true);
        try {
            await deactivateFieldMapRow(id);
            setFieldRows((rows) => rows.filter((r) => r.id !== id));
            flash('success', '✓ De-para removido.');
        } catch {
            flash('error', 'Erro ao remover.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="page-body"><p style={{ color: 'var(--text-secondary)' }}>Carregando pipelines e campos do GHL…</p></div>;
    }

    return (
        <div className="page-body">
            {status.text && <div className={`alert alert-${status.type}`}>{status.text}</div>}

            {/* ── Seção 1: Colunas-Gatilho ─────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div>
                    <h2 style={{ fontSize: '1rem', margin: 0 }}>Colunas-Gatilho</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>
                        Quando o card entra em uma coluna marcada, o evento é gravado no banco (via dashboard, ~1 min).
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button variant="secondary" onClick={carregar} isLoading={isLoading}><RefreshCw size={14} /> Recarregar</Button>
                    <Button onClick={handleSalvarGatilhos} isLoading={isSaving}>Salvar gatilhos</Button>
                </div>
            </div>

            {pipelines.map((p) => (
                <details key={p.id} open={stageRows.some((r) => r.pipeline_id === p.id && r.evento)} style={{ marginBottom: '0.75rem' }}>
                    <summary style={{ cursor: 'pointer', padding: '0.5rem 0', fontWeight: 600, fontSize: '0.875rem' }}>
                        {p.name}
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                            {stageRows.filter((r) => r.pipeline_id === p.id && r.evento).length} gatilho(s)
                        </span>
                    </summary>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...headCell, textAlign: 'left', width: '40%' }}>Coluna (estágio)</th>
                                <th style={{ ...headCell, textAlign: 'left' }}>Evento</th>
                                <th style={{ ...headCell, textAlign: 'left', width: '8rem' }}>Rodada</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stageRows.filter((r) => r.pipeline_id === p.id).map((r) => (
                                <tr key={r.stage_id} style={r.evento ? { background: 'rgba(0,220,130,0.04)' } : undefined}>
                                    <td style={cell}>{r.nome}</td>
                                    <td style={cell}>
                                        <Select value={r.evento} onChange={(e) => setStage(r.stage_id, { evento: e.target.value })}>
                                            {EVENTOS.map((ev) => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
                                        </Select>
                                    </td>
                                    <td style={cell}>
                                        <Select
                                            value={r.rodada ?? ''}
                                            onChange={(e) => setStage(r.stage_id, { rodada: e.target.value ? Number(e.target.value) : null })}
                                            disabled={!r.evento || ['venda', 'venda_perdida', 'ignorar'].includes(r.evento)}
                                        >
                                            <option value="">—</option>
                                            <option value="1">R1</option>
                                            <option value="2">R2</option>
                                            <option value="3">R3</option>
                                        </Select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            ))}

            {/* ── Seção 2: De-Para de Campos ───────────────────────────────── */}
            <div style={{ marginTop: '2rem', marginBottom: '0.5rem' }}>
                <h2 style={{ fontSize: '1rem', margin: 0 }}>De-Para de Campos</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>
                    Qual campo personalizado do GHL alimenta qual informação no banco, por evento.
                </p>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                <thead>
                    <tr>
                        <th style={{ ...headCell, textAlign: 'left' }}>Campo no GHL</th>
                        <th style={{ ...headCell, textAlign: 'left' }}>Evento</th>
                        <th style={{ ...headCell, textAlign: 'left' }}>Destino no banco</th>
                        <th style={{ ...headCell, textAlign: 'left', width: '5rem' }}>Tipo</th>
                        <th style={{ ...headCell, width: '3rem' }} />
                    </tr>
                </thead>
                <tbody>
                    {fieldRows.map((f) => (
                        <tr key={f.id}>
                            <td style={cell}>{fieldById.get(f.ghl_field_id)?.name ?? f.ghl_field_key ?? f.ghl_field_id}</td>
                            <td style={cell}>{EVENTOS.find((e) => e.value === f.evento)?.label ?? f.evento}</td>
                            <td style={cell}>{DESTINOS.find((d) => d.tabela === f.destino_tabela && d.coluna === f.destino_coluna)?.label ?? `${f.destino_tabela}.${f.destino_coluna}`}</td>
                            <td style={cell}>{f.tipo_esperado ?? '—'}</td>
                            <td style={{ ...cell, textAlign: 'center' }}>
                                <button onClick={() => handleRemoveField(f.id)} title="Remover" style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                    <Trash2 size={14} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    {fieldRows.length === 0 && (
                        <tr><td colSpan={5} style={{ ...cell, textAlign: 'center', color: 'var(--text-secondary)' }}>Nenhum de-para configurado.</td></tr>
                    )}
                </tbody>
            </table>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <FormGroup label="Campo do GHL">
                    <Select value={newField.ghl_field_id} onChange={(e) => setNewField((n) => ({ ...n, ghl_field_id: e.target.value }))}>
                        <option value="">Selecione…</option>
                        {customFields.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.dataType})</option>)}
                    </Select>
                </FormGroup>
                <FormGroup label="Evento">
                    <Select value={newField.evento} onChange={(e) => setNewField((n) => ({ ...n, evento: e.target.value }))}>
                        {EVENTOS.filter((e) => e.value && e.value !== 'ignorar').map((ev) => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
                    </Select>
                </FormGroup>
                <FormGroup label="Destino">
                    <Select value={newField.destino} onChange={(e) => setNewField((n) => ({ ...n, destino: e.target.value }))}>
                        <option value="">Selecione…</option>
                        {DESTINOS.map((d) => <option key={`${d.tabela}.${d.coluna}`} value={`${d.tabela}.${d.coluna}`}>{d.label}</option>)}
                    </Select>
                </FormGroup>
                <Button onClick={handleAddField} isLoading={isSaving}><Plus size={14} /> Adicionar</Button>
            </div>
        </div>
    );
}
